import { Router } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { PageContainer } from '../../../layout/Layout';
import { NotFound } from '../../../NotFound';
import { ErrorBox } from '../../../shared/ErrorBox';
import { PageLoadingIndicator } from '../../../shared/LoadingIndicator';
import { CreateDesign } from '../../designs/create-design/CreateDesign';
import { CreateDesignRevision } from '../../designs/create-revision/CreateDesignRevision';
import { DataForDesignDrawings } from '../../designs/data-for-design-drawings/DataForDesignDrawings';
import { EditDesignBillOfMaterials } from '../../designs/edit-bill-of-materials/EditDesignBillOfMaterials';
import { EditDesign } from '../../designs/edit-design/EditDesign';
import { EditPdfDetails } from '../../designs/edit-pdf-details/EditPdfDetails';
import { ViewDesignHandoverPack } from '../../designs/view-handover-pack/ViewDesignHandoverPack';
import { CreateQuote } from '../../quotes/create-quote/CreateQuote';
import { CreateQuoteRevision } from '../../quotes/create-revision/CreateQuoteRevision';
import { ViewOrEditQuoteBillOfMaterials } from '../../quotes/view-or-edit-bill-of-materials/ViewOrEditQuoteBillOfMaterials';
import { ViewOrEditQuote } from '../../quotes/view-or-edit-quote/ViewOrEditQuote';
import { ProjectDetails } from '../project-details/ProjectDetails';
import { projectRoutes } from '../projectRoutes';
import { ProjectContext } from './ProjectContext';
import { Project } from './ProjectHeader';

type Props = RouteComponentProps<{ projectId: string }>;

export const ProjectSection = ({ projectId }: Props) => {
  const { response, inProgress, error, sendRequest } = useApiRequest<Project>({
    httpMethod: 'get',
    endpointUrl: `projects/${projectId}`,
  });

  useEffect(() => {
    void sendRequest();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setProject(response);
  }, [response]);

  const [project, setProject] = useState<Project | null>(response);

  const reloadProject = () => {
    setProject(null);
    void sendRequest();
  };

  if (inProgress) {
    return <PageLoadingIndicator />;
  }

  if (error != null) {
    return (
      <PageContainer>
        <ErrorBox
          error={error}
          genericErrorMessage="An error occurred while attempting to load the project. Please try again."
        />
      </PageContainer>
    );
  }

  if (project != null) {
    return (
      <ProjectContext.Provider value={{ project, setProject, reloadProject }}>
        <Router primary={false}>
          <ProjectDetails path={projectRoutes.projectDetails} project={project} />
          <CreateQuote path={projectRoutes.newQuote} project={project} />
          <ViewOrEditQuote path={projectRoutes.viewOrEditQuote} project={project} />
          <CreateQuoteRevision path={projectRoutes.newQuoteRevision} project={project} />
          <ViewOrEditQuoteBillOfMaterials path={projectRoutes.viewOrEditQuoteBillOfMaterials} project={project} />
          <ViewDesignHandoverPack path={projectRoutes.viewDesignHandoverPack} />
          <CreateDesign path={projectRoutes.newDesignFromQuote} project={project} />
          <EditDesign path={projectRoutes.editDesign} project={project} />
          <CreateDesignRevision path={projectRoutes.newDesignRevision} project={project} />
          <EditDesignBillOfMaterials path={projectRoutes.viewOrEditDesignBillOfMaterials} project={project} />
          <EditPdfDetails path={projectRoutes.editPdfDetails} project={project} />
          <DataForDesignDrawings path={projectRoutes.dataForDesignDrawings} project={project} />
          <NotFound default={true} />
        </Router>
      </ProjectContext.Provider>
    );
  }

  return null;
};
